'use client';
import {
  ChangeEvent,
  createContext,
  FC,
  KeyboardEventHandler,
  ReactNode,
  useContext,
  useEffect
} from 'react';

import { LoginMeta, ReleaseGroup } from '@/components/login/login.types';
import useLoginWithSSO from '@/components/login/use-login-with-sso';
import useReleaseGroup from '@/components/login/use-release-group';
import useStandardLogin from '@/components/login/use-standard-login';
import { initPublicUrls, redirectAfterAuthentication } from '@/utils/redirect.utils';
import { checkSession } from '@/utils/requests.utils';

type LoginContextType = {
  ssoLoginErrorMessage: string;
  loginWithSso: () => void;
  isAuthenticatedWithSso: boolean;
  isLoginStandardLoading: boolean;
  isLoginWithSsoLoading: boolean;
  email: string;
  password: string;
  onPasswordChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onEmailChange: (event: ChangeEvent<HTMLInputElement>) => void;
  standardLogin: () => Promise<void>;
  standardLoginMetadata: LoginMeta;
  standardLoginErrorMessage: string;
  standardLoginWithEnter: KeyboardEventHandler<HTMLInputElement>;
  clearErrorMessage: () => void;
  releaseGroup: ReleaseGroup | undefined;
  setReleaseGroup: (releaseGroup: ReleaseGroup) => void;
};

export const LoginContext = createContext<LoginContextType>({} as LoginContextType);

export function useLoginContext() {
  return useContext(LoginContext);
}

export const LoginContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { releaseGroup, setReleaseGroup } = useReleaseGroup();
  const {
    ssoLoginErrorMessage,
    loginWithSso,
    isAuthenticatedWithSso,
    isLoginWithSsoLoading,
    clearSsoErrorMessage
  } = useLoginWithSSO();

  const {
    email,
    password,
    onPasswordChange,
    onEmailChange,
    standardLogin,
    isLoginStandardLoading,
    standardLoginMetadata,
    standardLoginErrorMessage,
    standardLoginWithEnter,
    clearStandardLoginErrorMessage
  } = useStandardLogin();

  useEffect(function init() {
    const initLogin = async () => {
      await initPublicUrls();
      const { releaseGroup: releaseGroupFromCheckSession } = await checkSession();
      if (releaseGroupFromCheckSession) {
        setReleaseGroup(releaseGroupFromCheckSession);
      }
    };
    initLogin();
  }, [setReleaseGroup]);

  useEffect(
    function redirectToClinicianPlatform() {
      if (releaseGroup) {
        redirectAfterAuthentication(releaseGroup);
      }
    },
    [releaseGroup]
  );

  const clearErrorMessage = () => {
    clearSsoErrorMessage();
    clearStandardLoginErrorMessage();
  };

  return (
    <LoginContext.Provider
      value={{
        releaseGroup,
        setReleaseGroup,
        ssoLoginErrorMessage,
        loginWithSso,
        isAuthenticatedWithSso,
        isLoginStandardLoading,
        isLoginWithSsoLoading,
        email,
        password,
        onPasswordChange,
        onEmailChange,
        standardLogin,
        standardLoginMetadata,
        standardLoginErrorMessage,
        standardLoginWithEnter,
        clearErrorMessage
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};
